/* eslint-disable eqeqeq */
/* eslint-disable react/destructuring-assignment */
/* eslint-disable no-case-declarations */
/* eslint-disable complexity */
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { randomHash } from '@lmig/dotcom-aspect-helpers/hash';
import parseStyle from '@lmig/dotcom-aspect-helpers/parseStyle';
import resolvePath from '@lmig/dotcom-aspect-helpers/resolvePath';
import { canHaveChildren, canHaveWhitespace } from '../constants/specialTags';
import ATTRIBUTES from '../constants/attributeNames';

/* eslint-disable consistent-return */

const validHtml = {
  div: { tagName: 'div', postChildren: '<br>' },
  p: { tagName: 'p' },
  ul: { tagName: 'ul' },
  ol: { tagName: 'ol' },
  li: { tagName: 'li' },
  Link: { tagName: 'a', href: 'href' },
  b: { tagName: 'b' },
  i: { tagName: 'i' },
  strong: { tagName: 'strong' },
  em: { tagName: 'em' },
  sup: { tagName: false },
  br: { tagName: 'br' },
};
class JsxParser extends Component {
  parseExpression = expression => {
    switch (expression.type) {
      case 'JSXAttribute':
        if (expression.value === null) return true;

        return this.parseExpression(expression.value);
      case 'JSXElement':
        return this.parseElement(expression);
      case 'JSXExpressionContainer':
        return this.parseExpression(expression.expression);
      case 'JSXText':
        const key = this.props.disableKeyGeneration ? undefined : randomHash();

        return this.props.disableFragments || this.props.renderToHtml ? (
          expression.value
        ) : (
          <Fragment key={key}>{expression.value}</Fragment>
        );
      case 'ArrayExpression':
        return expression.elements.map(this.parseExpression);

      case 'BinaryExpression':
        switch (expression.operator) {
          case '-':
            return this.parseExpression(expression.left) - this.parseExpression(expression.right);
          case '!=':
            return this.parseExpression(expression.left) != this.parseExpression(expression.right);
          case '!==':
            return this.parseExpression(expression.left) !== this.parseExpression(expression.right);
          case '*':
            return this.parseExpression(expression.left) * this.parseExpression(expression.right);
          case '**':
            return this.parseExpression(expression.left) ** this.parseExpression(expression.right);
          case '/':
            return this.parseExpression(expression.left) / this.parseExpression(expression.right);
          case '%':
            return this.parseExpression(expression.left) % this.parseExpression(expression.right);
          case '+':
            return this.parseExpression(expression.left) + this.parseExpression(expression.right);
          case '==':
            return this.parseExpression(expression.left) == this.parseExpression(expression.right);
          case '===':
            return this.parseExpression(expression.left) === this.parseExpression(expression.right);
          default:
            return undefined;
        }

      case 'CallExpression':
        const parsedCallee = this.parseExpression(expression.callee);

        if (parsedCallee === undefined) {
          this.props.onError(
            new Error(
              `The expression '${expression.callee}' could not be resolved, resulting in an undefined return value.`,
            ),
          );

          return undefined;
        }

        return parsedCallee(...expression.arguments.map(this.parseExpression));
      case 'ConditionalExpression':
        return this.parseExpression(expression.test)
          ? this.parseExpression(expression.consequent)
          : this.parseExpression(expression.alternate);
      case 'Identifier':
        return (this.props.bindings || {})[expression.name];
      case 'Literal':
        return expression.value;
      case 'LogicalExpression':
        const left = this.parseExpression(expression.left);
        if (expression.operator === '||' && left) return left;

        if ((expression.operator === '&&' && left) || (expression.operator === '||' && !left)) {
          return this.parseExpression(expression.right);
        }

        return false;
      case 'MemberExpression':
        const thisObj = this.parseExpression(expression.object) || {};
        const member = thisObj[expression.property.name];
        if (typeof member === 'function') return member.bind(thisObj);

        return member;
      case 'ObjectExpression':
        const object = {};
        expression.properties.forEach(prop => {
          object[prop.key.name || prop.key.value] = this.parseExpression(prop.value);
        });

        return object;

      case 'UnaryExpression':
        switch (expression.operator) {
          case '+':
            return expression.argument.value;
          case '-':
            return -1 * expression.argument.value;
          case '!':
            return !expression.argument.value;
          default:
            return undefined;
        }

      default:
        return undefined;
    }
  };

  parseName = element => {
    switch (element.type) {
      case 'JSXIdentifier':
        return element.name;
      case 'JSXMemberExpression':
        return `${this.parseName(element.object)}.${this.parseName(element.property)}`;
      default:
        return undefined;
    }
  };

  parseElement = element => {
    const { allowUnknownElements, components = {}, componentsOnly, onError } = this.props;
    const { children: childNodes = [], openingElement } = element;
    const { attributes = [] } = openingElement;
    const name = this.parseName(openingElement.name);

    if (!name) {
      onError(
        new Error(
          `The <${openingElement.name}> tag could not be parsed, and will not be rendered.`,
        ),
      );

      return undefined;
    }

    const blacklistedAttrs = (this.props.blacklistedAttrs || []).map(attr =>
      attr instanceof RegExp ? attr : new RegExp(attr, 'i'),
    );
    const whitelistedAttrs = (this.props.whitelistedAttrs || []).map(attr =>
      attr instanceof RegExp ? attr : new RegExp(attr, 'i'),
    );
    const blacklistedTags = (this.props.blacklistedTags || [])
      .map(tag => tag.trim().toLowerCase())
      .filter(Boolean);

    if (/^(html|head|body)$/i.test(name)) return childNodes.map(c => this.parseElement(c));
    const tagName = name.trim().toLowerCase();

    if (blacklistedTags.indexOf(tagName) !== -1) {
      onError(new Error(`The tag <${name}> is blacklisted, and will not be rendered.`));

      return undefined;
    }

    if (!resolvePath(components, name)) {
      if (componentsOnly) {
        onError(new Error(`The component <${name}> is unrecognized, and will not be rendered.`));

        return undefined;
      }

      if (!allowUnknownElements && document.createElement(name) instanceof HTMLUnknownElement) {
        onError(
          new Error(`The tag <${name}> is unrecognized in this browser, and will not be rendered.`),
        );

        return undefined;
      }
    }

    let children;
    const component = resolvePath(components, name);

    if (component || canHaveChildren(name) || this.props.renderToHtml) {
      children = childNodes.map(this.parseExpression);

      if (!component && !canHaveWhitespace(name)) {
        children = children.filter(child => typeof child !== 'string' || !/^\s*$/.test(child));
      }

      if (children.length === 0) {
        children = undefined;
      } else if (children.length === 1) {
        [children] = children;
      }
    }

    const props = {
      key: this.props.disableKeyGeneration ? undefined : randomHash(),
    };
    attributes.forEach(expr => {
      if (expr.type === 'JSXAttribute') {
        const rawName = expr.name.name;
        const attributeName = ATTRIBUTES[rawName] || rawName;
        // if the value is null, this is an implicitly "true" prop, such as readOnly
        const value = this.parseExpression(expr);

        const blacklistMatches = blacklistedAttrs.some(re => re.test(attributeName));
        const whitelistMatches = whitelistedAttrs.some(re => re.test(attributeName));

        if (!blacklistMatches || whitelistMatches) {
          if (value === 'true' || value === 'false') {
            props[attributeName] = value === 'true';
          } else {
            props[attributeName] = value;
          }
        }
      } else if (
        (expr.type === 'JSXSpreadAttribute' && expr.argument.type === 'Identifier') ||
        expr.argument.type === 'MemberExpression'
      ) {
        const value = this.parseExpression(expr.argument);

        if (typeof value === 'object') {
          Object.keys(value).forEach(rawName => {
            const attributeName = ATTRIBUTES[rawName] || rawName;
            const matches = blacklistedAttrs.filter(re => re.test(attributeName));

            if (matches.length === 0) {
              props[attributeName] = value[rawName];
            }
          });
        }
      }
    });

    if (typeof props.style === 'string') {
      props.style = parseStyle(props.style);
    }

    if (children) props.children = children;

    const ref = (refElement) => {
      if (refElement && childNodes) {
        // eslint-disable-next-line no-param-reassign
        refElement.childrenAst = childNodes;
      }
    };

    if (childNodes) ref.childrenAst = childNodes;

    if (this.props.renderToHtml) {
      const currentName = component || name;

      const outObject = validHtml[currentName];

      const outChildren = Array.isArray(props.children) ? props.children.join('') : props.children;

      if (!outObject) {
        return `${outChildren}`;
      }

      const outName = outObject.tagName;

      const outProps = Object.entries(props).reduce((acc, [propName, propValue]) => {
        if (['children', 'tagName'].includes(propName)) {
          return acc;
        }

        if (!Object.keys(outObject).includes(propName)) {
          return acc;
        }

        return `${acc} ${propName}="${propValue}"`;
      }, '');

      if (outName) {
        if (!props?.children) {
          return `<${outName}${outProps}>${outObject.postChildren || ''}</${outName}>`;
        }

        return `<${outName}${outProps}>${outChildren}${outObject.postChildren || ''}</${outName}>`;
      }

      return '';
    }

    return React.createElement(component || name.toLowerCase(), { ...props, ref });
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  render2 = () => {
    this.ParsedChildren = this.props.jsx
      .map(this.parseExpression)
      .filter(Boolean)
      .join('');

    return this.ParsedChildren;
  };

  render() {
    this.ParsedChildren = this.props.jsx.map(this.parseExpression).filter(Boolean);

    return this.props.renderInWrapper ? (
      <div className="jsx-parser">{this.ParsedChildren}</div>
    ) : (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>{this.ParsedChildren}</>
    );
  };
}

JsxParser.displayName = 'JsxParser';

JsxParser.propTypes = {
  allowUnknownElements: PropTypes.bool,
  bindings: PropTypes.shape({}),
  blacklistedAttrs: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(RegExp)]),
  ),
  blacklistedTags: PropTypes.arrayOf(PropTypes.string),
  components: PropTypes.shape({}),
  componentsOnly: PropTypes.bool,
  disableFragments: PropTypes.bool,
  disableKeyGeneration: PropTypes.bool,
  jsx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onError: PropTypes.func,
  renderInWrapper: PropTypes.bool,
  renderToHtml: PropTypes.bool,
  whitelistedAttrs: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(RegExp)]),
  ),
};

// Default props will continue to exist on class-based components
JsxParser.defaultProps = {
  allowUnknownElements: true,
  bindings: {},
  blacklistedAttrs: [/^on.+/i],
  blacklistedTags: ['script'],
  components: [],
  componentsOnly: false,
  disableFragments: false,
  disableKeyGeneration: false,
  jsx: '',
  onError: () => { },
  renderInWrapper: false,
  renderToHtml: false,
  whitelistedAttrs: [/^onBackground$/],
};

export default JsxParser;
